export const MAX_BATCH_TRANSFERS = 50;
export const MAX_BATCH_BYTES = 64 * 1024;
/** Bound for one registry lookup so a hung row cannot stall the sequential batch run. */
export const BATCH_CHECK_TIMEOUT_MS = 15_000;

export type BatchInputError = 'empty' | 'tooLarge' | 'tooMany' | 'malformed' | 'columns';
export type BatchRowError = 'invalidDomain' | 'missingCode' | 'invalidCode' | 'duplicate' | 'inCart';
export type BatchTransferRow = { id: number; domain: string; code: string };
export type BatchParseResult = { rows: BatchTransferRow[]; error?: never } | { rows?: never; error: BatchInputError };

/** CSV (including escaped quotes), European spreadsheet CSV, and spreadsheet paste. */
export function parseBatchTransfers(input: string): BatchParseResult {
  if (new TextEncoder().encode(input).length > MAX_BATCH_BYTES) return { error: 'tooLarge' };
  const text = input.replace(/^\uFEFF/, '').replace(/^(?:[ \t]*(?:\r\n|\r|\n))+/, '');
  if (!text.trim()) return { error: 'empty' };

  // Only an unquoted delimiter on the first record determines the dialect.
  let quoted = false;
  let delimiter = ',';
  for (const char of text) {
    if (char === '"') quoted = !quoted;
    if (!quoted && [',', ';', '\t'].includes(char)) {
      delimiter = char;
      break;
    }
    if (!quoted && /[\r\n]/.test(char)) break;
  }

  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let state: 'plain' | 'quoted' | 'closed' = 'plain';
  const finishRecord = () => {
    record.push(field);
    if (record.some((value) => value.trim())) records.push(record);
    record = [];
    field = '';
  };
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (state === 'quoted') {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else state = 'closed';
      } else field += char;
    } else if (char === delimiter) {
      record.push(field);
      field = '';
      state = 'plain';
    } else if (char === '\n' || char === '\r') {
      finishRecord();
      state = 'plain';
      if (char === '\r' && text[i + 1] === '\n') i++;
    } else if (state === 'closed') {
      if (char !== ' ') return { error: 'malformed' };
    } else if (char === '"') {
      if (field.trim()) return { error: 'malformed' };
      field = '';
      state = 'quoted';
    } else field += char;
  }
  if (state === 'quoted') return { error: 'malformed' };
  finishRecord();
  if (records[0]?.[0]?.trim().toLowerCase() === 'domain' && records[0]?.[1]?.trim().toLowerCase() === 'auth_code' && records[0].length === 2) records.shift();
  if (!records.length) return { error: 'empty' };
  if (records.length > MAX_BATCH_TRANSFERS) return { error: 'tooMany' };
  if (records.some((row) => row.length !== 2)) return { error: 'columns' };
  return { rows: records.map(([domain, code], id) => ({ id, domain: normalizeTransferDomain(domain), code: code.trim() })) };
}

export function normalizeTransferDomain(domain: string): string {
  return domain.trim().toLowerCase();
}

export function batchRowError(row: BatchTransferRow, rows: BatchTransferRow[], cartDomains: string[]): BatchRowError | undefined {
  const domain = normalizeTransferDomain(row.domain);
  // Only registrable .ch/.li names, including ASCII IDN (xn--) labels; never URLs or subdomains.
  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(ch|li)$/.test(domain)) return 'invalidDomain';
  if (!row.code.trim()) return 'missingCode';
  if (Array.from(row.code).some((char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) || row.code.length > 1024) return 'invalidCode';
  if (rows.filter((other) => normalizeTransferDomain(other.domain) === domain).length > 1) return 'duplicate';
  if (cartDomains.some((other) => normalizeTransferDomain(other) === domain)) return 'inCart';
}
